
import { useState, useEffect } from "react";

interface PaginationOptions {
  initialPage?: number;
  itemsPerPage?: number;
  totalItems: number;
}

export function usePagination({ 
  initialPage = 1, 
  itemsPerPage = 18, // Increased from 9 to 18 to show more prompts per page
  totalItems: initialTotalItems
}: PaginationOptions) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalItems, setTotalItems] = useState(initialTotalItems);
  
  // Update total items when prop changes
  useEffect(() => {
    if (initialTotalItems !== totalItems) {
      setTotalItems(initialTotalItems);
    }
  }, [initialTotalItems, totalItems]);
  
  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  
  // Reset to page 1 if current page is out of bounds after total changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Handle page changes
  const goToPage = (page: number) => {
    const targetPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(targetPage);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    currentPage,
    setCurrentPage: goToPage,
    totalPages,
    nextPage,
    prevPage,
    itemsPerPage,
    totalItems,
    setTotalItems
  };
}
